import { v4 as uuidv4 } from 'uuid';
import { action, computed, observable, runInAction } from 'mobx';
import { message } from 'antd';
import {
    DataCrafterTemplate,
    DataCrafterTemplateColumn,
    DataCrafterOutputVariant,
    CreateUpdateDataCrafterOutputVariantRequest
} from '../types';
import { DataCrafterTemplatesVisualStore } from '.';

/**
 * Store responsible for managing output variants functionality
 * Handles CRUD operations for output variants and dialog state management
 */
export default class DataCrafterOutputVariantsStore {
    // ========== Output Variants Properties ==========

    @observable
    outputVariants: DataCrafterOutputVariant[] = [];

    @observable
    selectedOutputVariant: DataCrafterOutputVariant | null = null;

    // ========== Output Variant Dialog Properties ==========

    @observable
    outputVariantDialogVisible: boolean = false;

    @observable
    editableOutputVariant: DataCrafterOutputVariant | null = null;

    constructor(private visualStore: DataCrafterTemplatesVisualStore) {}

    // ========== Computed Properties ==========

    @computed
    get hasOutputVariants(): boolean {
        return this.outputVariants.length > 0;
    }

    @computed
    get canCreateOutputVariant(): boolean {
        // This will be called with context from the form store
        return true; // Logic will be provided by form store
    }

    @computed
    get canManageOutputVariants(): boolean {
        // This will be called with context from the form store
        return true; // Logic will be provided by form store
    }

    // ========== Actions ==========

    @action.bound
    setSelectedOutputVariant(variant: DataCrafterOutputVariant | null) {
        this.selectedOutputVariant = variant;
    }

    @action.bound
    refreshSelectedOutputVariant() {
        const selected = this.selectedOutputVariant;
        if (!selected) {
            return;
        }
        this.outputVariants = this.outputVariants.map(variant =>
            variant.variantId === selected.variantId ? { ...selected } : variant
        );
        this.selectedOutputVariant =
            this.outputVariants.find(variant => variant.variantId === selected.variantId) || null;
    }

    @action.bound
    setOutputVariantDialogVisible(visible: boolean) {
        this.outputVariantDialogVisible = visible;
    }

    @action.bound
    setEditableOutputVariant(variant: DataCrafterOutputVariant | null) {
        this.editableOutputVariant = variant;
    }

    @action.bound
    openCreateOutputVariantDialog() {
        this.setEditableOutputVariant(null);
        this.setOutputVariantDialogVisible(true);
    }

    @action.bound
    openEditOutputVariantDialog(variant: DataCrafterOutputVariant) {
        this.setEditableOutputVariant(variant);
        this.setOutputVariantDialogVisible(true);
    }

    @action.bound
    closeOutputVariantDialog() {
        this.setOutputVariantDialogVisible(false);
        this.setEditableOutputVariant(null);
    }

    // ========== Output Variants Management ==========

    @action.bound
    async loadOutputVariants(template: DataCrafterTemplate) {
        if (!template || !template.outputVariants) {
            return;
        }

        try {
            const variants = template.outputVariants;

            // Initialize columns for each variant
            variants.forEach(variant => {
                variant.columns = variant.columns.map(
                    col =>
                        new DataCrafterTemplateColumn(
                            col.name,
                            col.worksheetName,
                            col.headerCoordinates,
                            col.packageId,
                            col.mappingRule
                        )
                );
            });

            runInAction(() => {
                this.outputVariants = variants;
            });
        } catch (error) {
            console.error('Failed to load output variants:', error);
            message.error('Failed to load output variants');
        }
    }

    @action.bound
    createOutputVariantLocal(
        data: CreateUpdateDataCrafterOutputVariantRequest,
        outputColumns: DataCrafterTemplateColumn[],
        templateId: string,
        tempTemplateId: string
    ): DataCrafterOutputVariant | null {
        try {
            // Generate a temporary ID for the new variant
            const tempId = uuidv4();
            const currentTime = new Date().toISOString();
            const currentUserId = this.visualStore.currentUserId || 'current-user';

            // Create new variant object
            const newVariant = new DataCrafterOutputVariant(
                data.name,
                currentTime,
                currentUserId,
                data.condition,
                outputColumns.map(
                    col =>
                        new DataCrafterTemplateColumn(
                            col.name,
                            col.worksheetName,
                            col.headerCoordinates,
                            col.packageId,
                            ''
                        )
                ),
                data.isDefault,
                '',
                templateId || tempTemplateId,
                tempId,
                undefined,
                undefined,
                data.scriptRefId
            );

            // If this is set as default, make sure no other variant is default
            if (data.isDefault) {
                this.outputVariants.forEach(variant => {
                    variant.isDefault = false;
                });
            }

            // Add to local variants array
            const newVariants = [...this.outputVariants, newVariant];
            this.outputVariants = newVariants;

            message.success('Output variant created successfully');
            return newVariant;
        } catch (error) {
            console.error('Failed to create output variant:', error);
            message.error('Failed to create output variant');
            return null;
        }
    }

    @action.bound
    updateOutputVariantLocal(
        variantId: string,
        data: CreateUpdateDataCrafterOutputVariantRequest
    ): DataCrafterOutputVariant | null {
        try {
            const variantIndex = this.outputVariants.findIndex(variant => variant.variantId === variantId);

            if (variantIndex === -1) {
                message.error('Variant not found');
                return null;
            }

            const existingVariant = this.outputVariants[variantIndex];
            const currentTime = new Date().toISOString();
            const currentUserId = this.visualStore.currentUserId || 'current-user';

            // If this is set as default, make sure no other variant is default
            if (data.isDefault) {
                this.outputVariants.forEach(variant => {
                    if (variant.variantId !== variantId) {
                        variant.isDefault = false;
                    }
                });
            }

            // Update the variant
            const updatedVariant = new DataCrafterOutputVariant(
                data.name,
                existingVariant.createdTime,
                existingVariant.createdBy,
                data.condition,
                data.columns,
                data.isDefault,
                existingVariant.code,
                existingVariant.dataCrafterTemplateId,
                existingVariant.variantId,
                currentTime,
                currentUserId,
                data.scriptRefId ?? existingVariant.scriptRefId
            );

            // Replace in array
            const newVariants = [...this.outputVariants];
            newVariants[variantIndex] = updatedVariant;
            this.outputVariants = newVariants;

            // Update selected variant if it's the one being edited
            if (this.selectedOutputVariant?.variantId === variantId) {
                this.setSelectedOutputVariant(updatedVariant);
            }

            message.success('Output variant updated successfully');
            return updatedVariant;
        } catch (error) {
            console.error('Failed to update output variant:', error);
            message.error('Failed to update output variant');
            return null;
        }
    }

    @action.bound
    deleteOutputVariantLocal(variantId: string): boolean {
        try {
            const variantIndex = this.outputVariants.findIndex(variant => variant.variantId === variantId);

            if (variantIndex === -1) {
                message.error('Variant not found');
                return false;
            }

            // Remove from array
            const newVariants = [...this.outputVariants];
            newVariants.splice(variantIndex, 1);
            this.outputVariants = newVariants;

            // Clear selected variant if it's the one being deleted
            if (this.selectedOutputVariant?.variantId === variantId) {
                this.setSelectedOutputVariant(null);
            }

            message.success('Output variant deleted successfully');
            return true;
        } catch (error) {
            console.error('Failed to delete output variant:', error);
            message.error('Failed to delete output variant');
            return false;
        }
    }

    @action.bound
    reset() {
        // Reset output variants data
        this.outputVariants = [];
        this.selectedOutputVariant = null;

        // Reset output variant dialog state
        this.outputVariantDialogVisible = false;
        this.editableOutputVariant = null;
    }

    // ========== Helper Methods ==========

    /**
     * Check if creating output variants is allowed based on external context
     */
    canCreateOutputVariantWithContext(
        selectedTemplate: DataCrafterTemplate | null,
        outputColumns: DataCrafterTemplateColumn[]
    ): boolean {
        return selectedTemplate !== null && outputColumns.length > 0 && outputColumns.some(col => col.mappingRule);
    }

    /**
     * Check if managing output variants is allowed based on external context
     */
    canManageOutputVariantsWithContext(selectedTemplate: DataCrafterTemplate | null): boolean {
        return selectedTemplate !== null;
    }
}
